// src/client/components/projects/ProjectTimeTable.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/client/components/ui/card';
import { InitialsAvatar } from '@/client/components/common/InitialsAvatar';
import type { RouterOutputs } from '@/client/utils/trpc';

const costFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

type TimeSummaryEntry = RouterOutputs['projects']['getMonthlyTimeSummary'][number];

interface Props {
  entries: TimeSummaryEntry[];
  monthLabel: string;
}

export function ProjectTimeTable({ entries, monthLabel }: Props) {
  if (entries.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Time & Costs · {monthLabel}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No time logged this month.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden p-0">
      <div className="border-b px-5 py-4">
        <h3 className="font-semibold">Time & Costs · {monthLabel}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th scope="col" className="px-5 pb-2 pt-3 font-medium">
                Person
              </th>
              <th scope="col" className="pb-2 pt-3 font-medium">
                Role
              </th>
              <th scope="col" className="pb-2 pt-3 pr-5 text-right font-medium">
                Hours
              </th>
              <th scope="col" className="pb-2 pt-3 pr-5 text-right font-medium">
                Rate
              </th>
              <th scope="col" className="pb-2 pt-3 pr-5 text-right font-medium">
                Cost
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {entries.map((e) => (
              <tr key={e.id}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <InitialsAvatar name={e.full_name || 'Unknown'} size={24} />
                    <span className="font-medium">{e.full_name || '—'}</span>
                  </div>
                </td>
                <td className="py-3 text-muted-foreground">{e.job_title || '—'}</td>
                <td className="py-3 pr-5 text-right tabular-nums">{e.hours}h</td>
                <td className="py-3 pr-5 text-right tabular-nums">
                  {e.rate != null ? `€${e.rate}/${e.rate_type === 'hourly' ? 'h' : 'mo'}` : '—'}
                </td>
                <td className="py-3 pr-5 text-right tabular-nums font-medium">
                  {e.cost != null ? `€${costFormatter.format(e.cost)}` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
